import { useFormikSubmit } from "@certn/api";
import { useAuth } from "@certn/auth";
import {
  BasicButton,
  ButtonGroup,
  Card,
  CheckboxFormikField,
  Grid,
  GridItem,
  Layout,
  Link,
  PrimaryButton,
  SecondaryTopNav,
  SectionMessage,
  Stack,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { CountrySelect } from "@certn/cascadia-react/patterns/address-form/CountrySelect";
import { getPrivacyPolicyUrl, getServiceAgreementUrl } from "@certn/utils";
import { css } from "@emotion/css";
import { Form, Formik, FormikHelpers } from "formik";
import { useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { bool, object, string } from "yup";

import { AppFooterLinks } from "./app-footer/AppFooterLinks";
import { IndustrySelect } from "./industry-select/IndustrySelect";
import { PermissiblePurposeSelect } from "./permissible-purpose/PermissiblePurposeSelect";
import { Schemas } from "../types";
import {
  currentRegion,
  regionToCountryCode,
  regions,
} from "../utils/regionSignUp";

export function getDefaultCompanyNameFromAuth0User(
  user: ReturnType<typeof useAuth>["user"]
): string {
  if (user && typeof user.org_name === "string") {
    return user.org_name;
  }
  return "";
}

interface Props {
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

export function RegionEntitlementSignUpForm(props: Props) {
  const { onClose, onSuccess } = props;
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const companyName = getDefaultCompanyNameFromAuth0User(user);

  const privacyPolicyUrl = getPrivacyPolicyUrl(i18n.language);
  const serviceAgreementUrl = getServiceAgreementUrl(i18n.language);
  const { submit } = useFormikSubmit(
    "/api/internal/client-portal/ssb/create-client-account/",
    {
      showToastOnError: true,
      onSuccess: async () => {
        await onSuccess?.();
      },
    }
  );

  const handleSubmit = useCallback(
    (
      values: Schemas["CreateClientAccount"],
      helpers: FormikHelpers<Schemas["CreateClientAccount"]>
    ) => {
      return submit(
        {
          ...values,
          service_terms_url: getServiceAgreementUrl(i18n.language),
          privacy_policy_url: getPrivacyPolicyUrl(i18n.language),
        },
        helpers
      );
    },
    [submit, i18n.language]
  );

  const defaultCountryCode = regionToCountryCode[currentRegion] || "";
  const regionName = regions[currentRegion];

  const schema = object({
    full_name: string().trim().required(),
    company_name: string().required().label(t("Company name")),
    country: string().trim().required().label(t("Country")),
    permissible_purpose: string()
      .trim()
      .required()
      .label(t("Reason for screening")),
    industry: string().trim().required().label(t("Industry")),
    business_identifier: string()
      .trim()
      .when([], {
        is: () => currentRegion === "au",
        then: (schema) =>
          schema.required(t("Australian Business Number is required")),
        otherwise: (schema) => schema.notRequired(),
      })
      .label(t("Australian Business Number")),
    agreed_to_terms_and_conditions: bool().required(),
  });

  return (
    <Formik<Schemas["CreateClientAccount"]>
      initialValues={{
        full_name: user?.name || "",
        company_name: companyName,
        country: defaultCountryCode,
        agreed_to_terms_and_conditions: false,
        service_terms_url: serviceAgreementUrl,
        privacy_policy_url: privacyPolicyUrl,
        industry: "",
        business_identifier: "",
        permissible_purpose: "EMPLOYMENT",
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ status, values, errors, touched, setFieldValue, isSubmitting }) => {
        return (
          <Form id="sign-up-form">
            <Layout
              layout="narrow"
              header={
                <SecondaryTopNav
                  testID="sign-up-region-header"
                  leftIcon="close"
                  onleftIconOnPress={onClose}
                >
                  <ButtonGroup>
                    <BasicButton onPress={onClose} isDisabled={isSubmitting}>
                      {t("Cancel")}
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      testID="sign-up-form-header-signup-button"
                      isLoading={isSubmitting}
                    >
                      {t("Sign up")}
                    </PrimaryButton>
                  </ButtonGroup>
                </SecondaryTopNav>
              }
              footer={<AppFooterLinks />}
            >
              <Card className={styles.card} hideBorder>
                <Stack orientation="vertical">
                  <Text style="global/title-small">
                    <Trans>Sign up to {regionName}</Trans>
                  </Text>
                  <Grid>
                    {status && (
                      <GridItem>
                        <SectionMessage
                          type="danger"
                          sectionTitle={t("System Error")}
                          icon="info"
                        >
                          {status}
                        </SectionMessage>
                      </GridItem>
                    )}
                    {!user?.name && (
                      <TextInputFormikField
                        isRequired
                        label={t("Full name")}
                        name="full_name"
                        testID="region-sign-up-full-name"
                      />
                    )}
                    <TextInputFormikField
                      isRequired
                      label={t("Company name")}
                      name="company_name"
                      testID="region-sign-up-company-name"
                    />
                    <GridItem>
                      <CountrySelect
                        label={t("Country")}
                        isRequired
                        testID="region-sign-up-country"
                        selectedKey={values.country}
                        onChange={(value) => setFieldValue("country", value)}
                        isInvalid={!!errors.country && touched.country}
                        errors={
                          errors.country && touched.country
                            ? errors.country
                            : undefined
                        }
                      />
                    </GridItem>

                    <GridItem>
                      <PermissiblePurposeSelect
                        name="permissible_purpose"
                        label={t("Reason for screening")}
                        isRequired
                        testID="sign-up-form-permissible-purpose"
                      />
                    </GridItem>
                    <GridItem>
                      <IndustrySelect
                        name="industry"
                        label={t("Industry")}
                        isRequired
                        testID="sign-up-form-industry"
                      />
                    </GridItem>
                    <GridItem>
                      <CheckboxFormikField
                        name="agreed_to_terms_and_conditions"
                        testID="region-sign-up-agreed-to-terms-and-conditions"
                      >
                        <Trans>
                          I have read and agree to Certn’s{" "}
                          <Link
                            testID="region-sign-up-privacy-policy"
                            href={privacyPolicyUrl}
                          >
                            Privacy Policy{" "}
                          </Link>
                          and{" "}
                          <Link
                            testID="region-sign-up-service-agreement"
                            href={serviceAgreementUrl}
                          >
                            Service Agreement
                          </Link>
                          .
                        </Trans>
                      </CheckboxFormikField>
                    </GridItem>
                    {currentRegion === "au" && (
                      <GridItem>
                        <TextInputFormikField
                          isRequired
                          label={t("Australian Business Number")}
                          name="business_identifier"
                          testID="sign-up-form-business-identifier"
                        />
                      </GridItem>
                    )}
                  </Grid>
                </Stack>
              </Card>
            </Layout>
          </Form>
        );
      }}
    </Formik>
  );
}

const styles = {
  card: css({
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
  }),
};
